import React from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { buildApiUrl } from './config/api';
import { schooldetails } from './redux/schooldetails';
import { buildPublicApplyUrl } from './utils/publicAdmissionApi';

const STATUS_COLORS = {
    payment_pending: 'default',
    submitted: 'info',
    under_review: 'warning',
    approved: 'success',
    rejected: 'error',
    enrolled: 'success',
};

function statusLabel(s) {
    return String(s || '').replace(/_/g, ' ');
}

export default function AdmissionApplications() {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin) || {};
    const schooldetails_ = useSelector((state) => state.schooldetails) || [];
    const school = schooldetails_[0] || {};

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [statusFilter, setStatusFilter] = React.useState('all');
    const [selected, setSelected] = React.useState(null);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [rejectOpen, setRejectOpen] = React.useState(false);
    const [rejectReason, setRejectReason] = React.useState('');
    const [reviewNotes, setReviewNotes] = React.useState('');
    const [snack, setSnack] = React.useState(null);
    const [actionLoading, setActionLoading] = React.useState(false);

    const [admissionsOpen, setAdmissionsOpen] = React.useState(false);
    const [academicYear, setAcademicYear] = React.useState('');
    const [settingsSaving, setSettingsSaving] = React.useState(false);

    const schoolID = admin.schoolID || school.schoolID;
    const [applyUrl, setApplyUrl] = React.useState('');

    React.useEffect(() => {
        if (schoolID) {
            setApplyUrl(buildPublicApplyUrl(schoolID));
        }
    }, [schoolID]);

    React.useEffect(() => {
        if (school) {
            setAdmissionsOpen(school.admissionsOpen === true);
            setAcademicYear(school.admissionAcademicYear || '');
        }
    }, [school]);

    const loadApplications = React.useCallback(async () => {
        if (!schoolID) return;
        setLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/list'), {
                schoolID,
                status: statusFilter,
            });
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Failed to load applications');
        } finally {
            setLoading(false);
        }
    }, [schoolID, statusFilter]);

    React.useEffect(() => {
        loadApplications();
    }, [loadApplications]);

    const openDetail = async (row) => {
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/detail'), {
                schoolID,
                id: row._id,
            });
            setSelected(res.data);
            setReviewNotes(res.data?.review?.notes || '');
            setDetailOpen(true);
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Failed to load detail');
        }
    };

    const runReview = async (action, extra = {}) => {
        if (!selected) return;
        setActionLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/review'), {
                schoolID,
                id: selected._id,
                action,
                notes: reviewNotes,
                rejectionReason: extra.rejectionReason,
                reviewerName: `${admin.lastname || ''} ${admin.othernames || ''}`.trim(),
                reviewerEmail: admin.email || admin.username || '',
            });
            setSelected(res.data.application);
            setSnack(`Application ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'updated'}`);
            setRejectOpen(false);
            loadApplications();
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Review failed');
        } finally {
            setActionLoading(false);
        }
    };

    const runEnroll = async () => {
        if (!selected) return;
        setActionLoading(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/enroll'), {
                schoolID,
                id: selected._id,
                reviewerName: `${admin.lastname || ''} ${admin.othernames || ''}`.trim(),
                reviewerEmail: admin.email || admin.username || '',
            });
            setSelected(res.data.application);
            setSnack(
                res.data.student
                    ? `Enrolled — Student ID: ${res.data.student.ID || res.data.application?.enrollment?.studentID}`
                    : 'Enrolled'
            );
            loadApplications();
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Enrollment failed');
        } finally {
            setActionLoading(false);
        }
    };

    const saveSettings = async () => {
        if (!schoolID) return;
        setSettingsSaving(true);
        try {
            const res = await axios.post(buildApiUrl('/api/admissions/admin/settings'), {
                schoolID,
                admissionsOpen,
                admissionAcademicYear: academicYear,
            });
            if (Array.isArray(res.data) && res.data.length > 0) {
                dispatch(schooldetails({ data: res.data }));
            }
            setSnack('Admission settings saved');
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Could not save settings');
        } finally {
            setSettingsSaving(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(applyUrl).then(() => setSnack('Apply link copied'));
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Typography variant="h5" gutterBottom>
                Admission Applications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review paid applications from your public admission form. Configure an Admission Fee charge on each class
                (Class → charges) before opening admissions.
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Public admissions
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={admissionsOpen}
                            onChange={(e) => setAdmissionsOpen(e.target.checked)}
                        />
                    }
                    label="Accept online applications"
                />
                <TextField
                    label="Academic year (shown on form)"
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value)}
                    size="small"
                    sx={{ display: 'block', mt: 1, mb: 1, maxWidth: 320 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    <TextField
                        label="Public apply link"
                        value={applyUrl || (schoolID ? 'Generating…' : 'Log in — school ID missing')}
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1, minWidth: 260 }}
                    />
                    <IconButton onClick={copyLink} aria-label="Copy link" disabled={!applyUrl}>
                        <ContentCopyIcon />
                    </IconButton>
                    <Button
                        variant="outlined"
                        disabled={!applyUrl}
                        onClick={() => applyUrl && window.open(applyUrl, '_blank', 'noopener,noreferrer')}
                    >
                        Open link
                    </Button>
                    <Button variant="contained" onClick={saveSettings} disabled={settingsSaving}>
                        {settingsSaving ? 'Saving…' : 'Save settings'}
                    </Button>
                </Box>
                {!schoolID && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        School ID not found in session. Log out and log in again.
                    </Typography>
                )}
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="submitted">Submitted</MenuItem>
                        <MenuItem value="under_review">Under review</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="enrolled">Enrolled</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="payment_pending">Payment pending</MenuItem>
                    </Select>
                </FormControl>
                <Button onClick={loadApplications} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ref #</TableCell>
                                <TableCell>Applicant</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>Fee (GHS)</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No applications yet
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row._id} hover>
                                        <TableCell>{row.applicationNumber}</TableCell>
                                        <TableCell>
                                            {row.lastname} {row.othernames}
                                        </TableCell>
                                        <TableCell>{row.classApplied}</TableCell>
                                        <TableCell>{row.feeGhs != null ? Number(row.feeGhs).toFixed(2) : '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={statusLabel(row.status)}
                                                color={STATUS_COLORS[row.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row.createdAt
                                                ? new Date(row.createdAt).toLocaleDateString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => openDetail(row)}>
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Application {selected?.applicationNumber}
                    {selected?.status && (
                        <Chip
                            size="small"
                            sx={{ ml: 1 }}
                            label={statusLabel(selected.status)}
                            color={STATUS_COLORS[selected.status] || 'default'}
                        />
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    {selected && (
                        <Box sx={{ display: 'grid', gap: 1.5 }}>
                            <Typography variant="subtitle2">Student</Typography>
                            <Typography variant="body2">
                                {selected.lastname} {selected.othernames} · {selected.gender} · DOB:{' '}
                                {selected.dob || '—'} · Class: {selected.classApplied}
                            </Typography>
                            <Typography variant="subtitle2">Guardian</Typography>
                            <Typography variant="body2">
                                {selected.guardianlastname} {selected.guardianothernames} ·{' '}
                                {selected.guardiancontact} · {selected.guardianemail || '—'}
                            </Typography>
                            {selected.payment?.paystackReference && (
                                <>
                                    <Typography variant="subtitle2">Payment</Typography>
                                    <Typography variant="body2">
                                        GHS {Number(selected.feeGhs || 0).toFixed(2)} · Ref:{' '}
                                        {selected.payment.paystackReference}
                                        {selected.payment.paidAt &&
                                            ` · ${new Date(selected.payment.paidAt).toLocaleString()}`}
                                    </Typography>
                                </>
                            )}
                            {selected.enrollment?.studentID && (
                                <Typography variant="body2" color="success.main">
                                    Enrolled — Student ID: {selected.enrollment.studentID}
                                </Typography>
                            )}
                            <TextField
                                label="Review notes (internal)"
                                multiline
                                minRows={2}
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                fullWidth
                                disabled={selected.status === 'enrolled' || selected.status === 'rejected'}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    {selected &&
                        ['submitted', 'under_review'].includes(selected.status) && (
                            <>
                                <Button
                                    onClick={() => runReview('under_review')}
                                    disabled={actionLoading}
                                >
                                    Mark under review
                                </Button>
                                <Button
                                    color="error"
                                    onClick={() => setRejectOpen(true)}
                                    disabled={actionLoading}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => runReview('approve')}
                                    disabled={actionLoading}
                                >
                                    Approve
                                </Button>
                            </>
                        )}
                    {selected && selected.status === 'approved' && (
                        <Button variant="contained" onClick={runEnroll} disabled={actionLoading}>
                            Enroll student
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
                <DialogTitle>Reject application</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Reason (optional)"
                        fullWidth
                        multiline
                        minRows={3}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => runReview('reject', { rejectionReason: rejectReason })}
                        disabled={actionLoading}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snack}
                autoHideDuration={6000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="info" onClose={() => setSnack(null)}>
                    {snack}
                </Alert>
            </Snackbar>
        </Box>
    );
}
